import { queryOptions, useQuery } from "@tanstack/react-query";

import {
  Dashboard,
  PublishedDashboard,
  api,
  TimeRangeSelectorSettings,
  TimeZoneSettings,
} from "@/lib/api";
import { unwrapAxiosData } from "@/lib/api/query-utils";

export const keys = {
  dashboards: () => ["dashboards"] as const,
  publishedDashboards: () => ["publishedDashboards"] as const,
  // Namespace prefix for everything scoped to a dashboard (detail, locations,
  // elements, warnings, ...). No query is keyed exactly at this; use it only to
  // invalidate the whole dashboard subtree on purpose.
  dashboard: (id: string) => ["dashboard", id] as const,
  // The dashboard record itself (GET /dashboards/:id), a leaf under the
  // namespace so it can be invalidated without touching sibling sub-resources.
  dashboardDetail: (id: string) => ["dashboard", id, "detail"] as const,
  timerange: (dashboardId: string) => ["timerange", dashboardId] as const,
  timezone: (dashboardId: string) => ["timezone", dashboardId] as const,
};

/*
  List of all dashboards query
*/
export const dashboardsQuery = () =>
  queryOptions({
    queryKey: keys.dashboards(),
    queryFn: (): Promise<Dashboard[]> => unwrapAxiosData(api.dashboard.getDashboards()),
  });

/*
  Single dashboard query
*/
export const dashboardQuery = (id: string) =>
  queryOptions({
    queryKey: keys.dashboardDetail(id),
    queryFn: (): Promise<Dashboard> => unwrapAxiosData(api.dashboard.getDashboard(id)),
  });

/*
 Query to get all published dashboards
*/
export const publishedDashboardsQuery = () => {
  return queryOptions({
    queryKey: keys.publishedDashboards(),
    queryFn: (): Promise<PublishedDashboard[]> =>
      unwrapAxiosData(api.published.getPublishedDashboards()),
  });
};

/*
  Hook to get all published dashboards
*/
export const usePublishedDashboardsQuery = () => {
  return useQuery(publishedDashboardsQuery());
};

export const useTimeRangeSelectorQuery = (dashboardId: string) => {
  return useQuery({
    queryKey: keys.timerange(dashboardId),
    queryFn: (): Promise<TimeRangeSelectorSettings> =>
      unwrapAxiosData(
        api.timeRangeSelector.getTimeRangeSelectorSettingsForDashboard(dashboardId),
      ),
  });
};

export const useTimezoneQuery = (dashboardId: string) => {
  return useQuery({
    queryKey: keys.timezone(dashboardId),
    queryFn: (): Promise<TimeZoneSettings> =>
      unwrapAxiosData(api.timezones.getTimeZoneSettingsForDashboard(dashboardId)),
  });
};
